import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User, UserRole, DeliveryPerson } from '../types/models';
import { auditService } from '../services/auditService';
import { logger } from '../services/loggerService';
import { AuditAction } from '../types/audit';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  deliveryPerson: DeliveryPerson | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  isDeliveryPerson: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper function to convert Firestore data
const convertTimestampFields = (data: any): any => {
  const converted = { ...data };
  Object.keys(converted).forEach(key => {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toDate();
    }
  });
  return converted;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [deliveryPerson, setDeliveryPerson] = useState<DeliveryPerson | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Log successful login
      await auditService.logAuth({
        action: AuditAction.LOGIN,
        userEmail: email,
        userName: userCredential.user.displayName || email.split('@')[0],
        userId: userCredential.user.uid,
        success: true,
      });
      
      logger.info('User logged in successfully', { email });
    } catch (error: any) {
      // Log failed login attempt
      await auditService.logAuth({
        action: AuditAction.LOGIN_FAILED,
        userEmail: email,
        success: false,
        errorMessage: error.message,
      });
      
      logger.error('Login failed', error, { component: 'AuthContext', action: 'login' });
      throw new Error(error.message);
    }
  };

  const register = async (email: string, password: string): Promise<void> => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const email = currentUser?.email || 'unknown';
      const userId = currentUser?.uid || 'unknown';
      
      // Clear local state first
      setUserProfile(null);
      setDeliveryPerson(null);
      setCurrentUser(null);
      
      // Then sign out from Firebase
      await signOut(auth);
      
      // Log logout
      await auditService.logAuth({
        action: AuditAction.LOGOUT,
        userEmail: email,
        userId: userId,
        success: true,
      });
      
      logger.info('User logged out successfully', { email });
    } catch (error: any) {
      logger.error('Logout failed', error, { component: 'AuthContext' });
      throw new Error(error.message);
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
      
      // Log password reset request
      await auditService.logAuth({
        action: AuditAction.PASSWORD_RESET,
        userEmail: email,
        success: true,
      });
      
      logger.info('Password reset email sent', { email });
    } catch (error: any) {
      logger.error('Password reset failed', error, { component: 'AuthContext' });
      throw new Error(error.message);
    }
  };

  // Function to refresh current user data
  const refreshUserData = async (): Promise<void> => {
    if (currentUser) {
      await fetchUserData(currentUser);
    }
  };


  // Function to fetch user profile and delivery person data
  const fetchUserData = async (firebaseUser: FirebaseUser | null) => {
    if (!firebaseUser) {
      setUserProfile(null);
      setDeliveryPerson(null);
      setLoading(false);
      return;
    }

    try {
      // Fetch user profile from users collection
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const rawUserData = userDocSnap.data();
        const userData = convertTimestampFields(rawUserData) as User;
        setUserProfile({ ...userData, id: userDocSnap.id });
        
        // If user is a delivery person, fetch delivery person data
        if (userData.role === UserRole.DELIVERY && userData.deliveryPersonId) {
          const deliveryPersonDocRef = doc(db, 'deliveryPersons', userData.deliveryPersonId);
          const deliveryPersonDocSnap = await getDoc(deliveryPersonDocRef);
          
          if (deliveryPersonDocSnap.exists()) {
            const rawDeliveryPersonData = deliveryPersonDocSnap.data();
            const deliveryPersonData = convertTimestampFields(rawDeliveryPersonData) as DeliveryPerson;
            setDeliveryPerson({ ...deliveryPersonData, id: deliveryPersonDocSnap.id });
          } else {
            logger.warn(`Delivery person document not found for ID: ${userData.deliveryPersonId}`);
            setDeliveryPerson(null);
          }
        } else {
          setDeliveryPerson(null);
        }
      } else {
        logger.warn(`User document not found for UID: ${firebaseUser.uid}`);
        setUserProfile(null);
        setDeliveryPerson(null);
      }
    } catch (error) {
      logger.error('Error fetching user data', error, { component: 'AuthContext' });
      setUserProfile(null);
      setDeliveryPerson(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      fetchUserData(user);
    });

    return unsubscribe;
  }, []);

  // Computed properties for role checking
  const isAdmin = userProfile?.role === UserRole.ADMIN;
  const isDeliveryPerson = userProfile?.role === UserRole.DELIVERY;

  const value: AuthContextType = {
    currentUser,
    userProfile,
    deliveryPerson,
    login,
    register,
    logout,
    resetPassword,
    refreshUserData,
    loading,
    isAdmin,
    isDeliveryPerson
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
